import { DataTable } from '@/components/DataTable';
import { ErrorBoundaryFallbackRender } from '@/components/ui/ErrorBoundaryFallback';
import { OrderBy, Site, SiteBoolExp, SiteOrderBy } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import useDataTable from '@/hooks/useDataTable';
import { useSyncFiltersToSearchParams } from '@/hooks/useSyncFiltersToSearchParams';
import { SearchParam } from '@/models/search-param.models';
import { searchParamToTableSortingState } from '@/utils/search-param.function';
import { toSqlLikeString } from '@/utils/string';
import { useQuery } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router';
import { validate } from 'uuid';
import ListSiteHeader, { SiteFilter } from '../components/ListSiteHeader';
import { siteColumns } from '../components/SiteColumns';
import { siteQuery } from '../graphql/query';

type SiteQueryFilterInput = {
  where: SiteBoolExp;
  limit: number;
  offset: number;
  orderBy: SiteOrderBy[];
};

export default function ListSitePage() {
  const DEFAULT_PAGE_SIZE = 50;
  const DEFAULT_DATA_ORDER: SiteOrderBy[] = [
    {
      createdAt: OrderBy.Desc,
    },
  ];

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [filterFormsData, setFilterFormsData] = useState<SiteFilter>();

  const [filters, setFilters] = useState<SiteQueryFilterInput>({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    orderBy: DEFAULT_DATA_ORDER,
    where: {},
  });

  const [searchParams, _setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  // This state indicate when if the search params are loaded after the page load.
  const [searchParamsLoaded, setSearchParamsLoaded] = useState<boolean>(false);

  useSyncFiltersToSearchParams<SiteFilter>({
    filters: filterFormsData,
    pagination,
    sorting,
    setPagination,
    searchParamsLoaded,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['Sites', filters],
    queryFn: () =>
      execute({
        query: siteQuery,
        variables: {
          limit: filters.limit,
          offset: filters.offset,
          where: filters.where,
          orderBy: filters.orderBy,
        },
      }),
    select: (data) => data,
    staleTime: 3000,
    enabled: searchParamsLoaded,
  });

  useEffect(() => {
    if (data) {
      setSites((data.site ?? []) as Site[]);
      setRowCount((data.siteAggregate?.aggregate?.count as number) ?? 0);
    }
  }, [data]);

  const table = useDataTable({
    data: sites,
    columns: siteColumns,
    rowCount,
    sorting,
    pagination,
    setSorting,
    setPagination,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
  });

  useEffect(() => {
    const name = searchParams.get(SearchParam.NAME);
    const customer = searchParams.get(SearchParam.CUSTOMER);
    const notesParam = searchParams.get(SearchParam.NOTES);
    const notes =
      notesParam === 'yes' || notesParam === 'no' ? notesParam : null;
    const pageSize = parseInt(
      searchParams.get(SearchParam.PAGE_SIZE) ?? DEFAULT_PAGE_SIZE.toString(),
    );
    const pageIndex = parseInt(searchParams.get(SearchParam.PAGE) ?? '0');
    const orderBy = searchParams.get(SearchParam.ORDER_BY);
    const limit = pageSize;
    const offset = pageIndex * limit;
    const sortingState = searchParamToTableSortingState(orderBy);

    if (!searchParamsLoaded) {
      setFilterFormsData({
        name,
        customer,
        notes,
      });
      setSorting(sortingState);
      setPagination({
        pageIndex,
        pageSize,
      });
    }

    // Create Site Where Filter from SearchParams.
    const andFilters: SiteBoolExp[] = [];
    if (name) {
      if (validate(name)) {
        andFilters.push({ id: { _eq: name } });
      } else {
        andFilters.push({ name: { _ilike: toSqlLikeString(name) } });
      }
    }

    if (customer) {
      andFilters.push({
        customer: {
          id: {
            _eq: customer,
          },
        },
      });
    }

    if (notes === 'yes') {
      andFilters.push({
        userData: { _contains: { notes: {} } },
      });
    } else if (notes === 'no') {
      andFilters.push({
        _or: [
          { userData: { _isNull: true } },
          { _not: { userData: { _contains: { notes: {} } } } },
        ],
      });
    }
    // Convert the react table SortingState to Site Query OrderBy
    const orderByFromParams = sortingState.map((sort): SiteOrderBy => {
      const order = sort.desc ? OrderBy.DescNullsLast : OrderBy.AscNullsLast;
      if (sort.id === 'customer') {
        return {
          customer: {
            name: order,
          },
        };
      }
      return {
        [sort.id]: order,
      };
    });

    setSearchParamsLoaded(true);
    setFilters({
      limit,
      offset,
      where: {
        _and: andFilters,
      },
      orderBy: orderByFromParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <ErrorBoundary fallbackRender={ErrorBoundaryFallbackRender}>
      <ListSiteHeader
        setFilters={setFilterFormsData}
        table={table}
        filters={filterFormsData}
      />
      <DataTable table={table} loading={isLoading} fetching={isFetching} />
    </ErrorBoundary>
  );
}
